import QRCode from 'qrcode';

/**
 * Compute a doctor's display initials from their full name.
 * "Dr. Chetna Tikoo" → "CT" · "Dr Rajesh K Sharma" → "RKS" (capped at 3).
 * Falls back to "DR" if the input doesn't yield anything usable.
 */
export function doctorInitials(name) {
  if (!name) return 'DR';
  const cleaned = String(name).replace(/^Dr\.?\s+/i, '').trim();
  const parts = cleaned.split(/\s+/).filter(Boolean);
  const letters = parts.map(p => p[0]?.toUpperCase()).filter(Boolean).slice(0, 3).join('');
  return letters || 'DR';
}

/**
 * Render a clinic Check-In QR as a PNG dataURL with strong doctor branding:
 *   - Optional top header (e.g. "PATIENT SELF-CHECKIN CLINIC QR") baked into the print.
 *   - Doctor's initials ("Dr CT") rendered as a bold badge in the centre of the QR.
 *   - Clinic / hospital name footer beneath the QR.
 *
 * Uses QR error-correction level "H" (~30% redundancy) so the centre badge
 * can never break scanning. Badge occupies ~24% of QR width — comfortably
 * within QR-H tolerance.
 *
 * @param {string} url           URL to encode
 * @param {object} opts
 * @param {string} opts.label    Clinic / hospital name printed under the QR
 * @param {string} opts.initials Bold text rendered in the centre of the QR
 *                               (typically `"Dr " + doctorInitials(name)`).
 * @param {string} opts.topHeader Optional banner text baked above the QR
 *                               (e.g. "PATIENT SELF-CHECKIN CLINIC QR").
 * @param {string} opts.attribution Optional "Powered by …" line printed at
 *                               the very bottom (defaults to "DocSite Platform™").
 * @param {string} opts.attributionLogoSrc Tiny logo printed before the
 *                               attribution text (defaults to /docsite-logo.png).
 * @param {number} opts.size     Output canvas width in px (default 1200)
 * @returns {Promise<string>}    PNG dataURL
 */
export async function buildBrandedQrPng(url, opts = {}) {
  const {
    label = '',
    initials = 'DR',
    topHeader = '',
    benefit = '',
    attribution = 'Powered by DocSite Platform\u2122',
    attributionLogoSrc = '/docsite-logo.png',
    size = 1200,
  } = opts;

  const qrDataUrl = await QRCode.toDataURL(url, {
    width: size,
    margin: 2,
    errorCorrectionLevel: 'H',
    color: { dark: '#0f172a', light: '#ffffff' },
  });
  const qrImg = await loadImage(qrDataUrl);
  let attribLogo = null;
  if (attribution && attributionLogoSrc) {
    try { attribLogo = await loadImage(attributionLogoSrc); } catch { /* optional */ }
  }

  const headerHeight = topHeader ? Math.round(size * 0.13) : 0;
  const benefitHeight = benefit ? Math.round(size * 0.085) : 0;
  const footerHeight = label ? Math.round(size * 0.11) : 0;
  const attribHeight = attribution ? Math.round(size * 0.12) : 0;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = headerHeight + benefitHeight + size + footerHeight + attribHeight;
  const ctx = canvas.getContext('2d');

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // --- Top header band ---
  if (topHeader && headerHeight > 0) {
    // Subtle sky tinted band so the header reads as a banner, not a heading.
    ctx.fillStyle = '#0369a1';
    ctx.fillRect(0, 0, size, headerHeight);
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const family = 'Inter, system-ui, -apple-system, "Segoe UI", sans-serif';
    let fontPx = Math.round(headerHeight * 0.42);
    const maxWidth = size * 0.92;
    let text = topHeader;
    ctx.font = `800 ${fontPx}px ${family}`;
    while (ctx.measureText(text).width > maxWidth && fontPx > 22) {
      fontPx -= 2;
      ctx.font = `800 ${fontPx}px ${family}`;
    }
    ctx.fillText(text, size / 2, headerHeight / 2);
  }

  // --- Benefit sub-banner (emerald, patient-facing entitlement line) ---
  // Mixed-weight one-liner: bold-caps "DIGITAL HEALTH FILE" + bold "same day",
  // the rest medium. Drawn segment-by-segment (canvas can't mix weights in a
  // single fillText) and auto-shrunk so it always stays on one line.
  if (benefit && benefitHeight > 0) {
    ctx.fillStyle = '#ecfdf5'; // emerald-50 tint so it reads as a soft promise
    ctx.fillRect(0, headerHeight, size, benefitHeight);
    const family = 'Inter, system-ui, -apple-system, "Segoe UI", sans-serif';
    const segments = [
      { t: 'DIGITAL HEALTH FILE', w: 800 },
      { t: ' for ', w: 500 },
      { t: 'same day', w: 700 },
      { t: ' CHECK-IN only. ', w: 500 },
      { t: 'FREE.', w: 800 },
    ];
    let fontPx = Math.round(benefitHeight * 0.53);
    const maxWidth = size * 0.94;
    const totalWidth = (px) => segments.reduce((acc, s) => {
      ctx.font = `${s.w} ${px}px ${family}`;
      return acc + ctx.measureText(s.t).width;
    }, 0);
    while (totalWidth(fontPx) > maxWidth && fontPx > 14) fontPx -= 2;
    ctx.fillStyle = '#047857'; // emerald-700 for the text
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    let x = (size - totalWidth(fontPx)) / 2;
    const y = headerHeight + benefitHeight / 2;
    for (const s of segments) {
      ctx.font = `${s.w} ${fontPx}px ${family}`;
      ctx.fillText(s.t, x, y);
      x += ctx.measureText(s.t).width;
    }
  }

  // --- QR ---
  ctx.drawImage(qrImg, 0, headerHeight + benefitHeight, size, size);

  // --- Centre initials badge (replaces the platform logo) ---
  if (initials) {
    const badgeSize = Math.round(size * 0.24);
    const r = Math.round(size * 0.045);
    const bx = (size - badgeSize) / 2;
    const by = headerHeight + benefitHeight + (size - badgeSize) / 2;

    // White rounded-rect with a thin slate-900 border so the badge reads
    // as a proper card, not a void inside the QR.
    drawRoundedRect(ctx, bx, by, badgeSize, badgeSize, r);
    ctx.fillStyle = '#ffffff';
    ctx.fill();
    drawRoundedRect(ctx, bx + 4, by + 4, badgeSize - 8, badgeSize - 8, Math.max(0, r - 4));
    ctx.lineWidth = Math.max(2, Math.round(size * 0.004));
    ctx.strokeStyle = '#0f172a';
    ctx.stroke();

    // Initials text — bold, slate-900, auto-fit within the badge.
    ctx.fillStyle = '#0f172a';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const family = 'Inter, system-ui, -apple-system, "Segoe UI", sans-serif';
    const maxWidth = badgeSize * 0.78;
    let fontPx = Math.round(badgeSize * 0.42);
    ctx.font = `800 ${fontPx}px ${family}`;
    while (ctx.measureText(initials).width > maxWidth && fontPx > 18) {
      fontPx -= 2;
      ctx.font = `800 ${fontPx}px ${family}`;
    }
    ctx.fillText(initials, bx + badgeSize / 2, by + badgeSize / 2);
  }

  // --- Footer label (clinic / hospital name) ---
  if (label && footerHeight > 0) {
    ctx.fillStyle = '#0f172a';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const maxWidth = size * 0.92;
    let fontPx = Math.round(footerHeight * 0.50);
    const family = 'Inter, system-ui, -apple-system, "Segoe UI", sans-serif';
    let text = label;
    ctx.font = `600 ${fontPx}px ${family}`;
    while (ctx.measureText(text).width > maxWidth && fontPx > 18) {
      fontPx -= 2;
      ctx.font = `600 ${fontPx}px ${family}`;
    }
    if (ctx.measureText(text).width > maxWidth) {
      while (text.length > 4 && ctx.measureText(text + '…').width > maxWidth) text = text.slice(0, -1);
      text = text + '…';
    }
    ctx.fillText(text, size / 2, headerHeight + benefitHeight + size + footerHeight / 2);
  }

  // --- Attribution band (logo + "Powered by DocSite Platform™") ---
  if (attribution && attribHeight > 0) {
    const baseY = headerHeight + benefitHeight + size + footerHeight;
    const family = 'Inter, system-ui, -apple-system, "Segoe UI", sans-serif';
    ctx.fillStyle = '#334155';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';

    const logoSize = attribLogo ? Math.round(attribHeight * 0.95) : 0;
    const gap = attribLogo ? Math.round(size * 0.015) : 0;
    const maxTotalWidth = size * 0.92;

    // Shrink-to-fit so long attributions (e.g. "Powered by DocSite Platform™")
    // never overrun the canvas width. Without this the text gets clipped to
    // "Powered by DocSite Platf" on default canvas sizes.
    let fontPx = Math.round(attribHeight * 0.55);
    ctx.font = `700 ${fontPx}px ${family}`;
    while (logoSize + gap + ctx.measureText(attribution).width > maxTotalWidth && fontPx > 20) {
      fontPx -= 2;
      ctx.font = `700 ${fontPx}px ${family}`;
    }
    const textWidth = ctx.measureText(attribution).width;
    const totalWidth = logoSize + gap + textWidth;
    const startX = Math.max(0, Math.round((size - totalWidth) / 2));
    const midY = baseY + attribHeight / 2;

    if (attribLogo) {
      ctx.drawImage(attribLogo, startX, midY - logoSize / 2, logoSize, logoSize);
    }
    ctx.fillText(attribution, startX + logoSize + gap, midY);
  }

  return canvas.toDataURL('image/png');
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function drawRoundedRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}
